import React, { useState, useEffect } from "react";
import { User, Mail, Building2, Briefcase, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../auth/hooks/useAuth";
import { authService } from "../auth/services/authService";
import { adminService } from "../admin/services/adminService";
import PasswordStrengthIndicator, { isPasswordStrong } from "../../components/ui/PasswordStrengthIndicator";

const inputClass =
  "w-full pl-9 pr-4 py-2.5 border border-surface-border rounded-lg text-sm " +
  "text-brand-text placeholder-brand-light bg-white focus:outline-none " +
  "focus:ring-2 focus:ring-primary/30 focus:border-primary transition";

const FieldLabel = ({ children }) => (
  <label className="block text-sm font-medium text-brand-text mb-1.5">{children}</label>
);

const IconWrap = ({ children }) => (
  <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-light">
    {children}
  </span>
);

// ── Profile tab ───────────────────────────────────────────────────────────────
const ProfileTab = () => {
  const { state, updateProfile } = useAuth();
  const user = state.user || {};

  // Always fetch fresh profile so plan_tier reflects current subscription
  const [, setLiveUser] = useState(user);
  const [currentPlanName, setCurrentPlanName] = useState('');
  useEffect(() => {
    authService.getProfile()
      .then((res) => {
        const fresh = res?.data?.user ?? res?.data ?? res?.user ?? null;
        if (fresh) setLiveUser((prev) => ({ ...prev, ...fresh }));
      })
      .catch(() => { /* fall back to stored user */ });

    // Fetch the actual current subscribed plan name from the plans API
    adminService.getPlans()
      .then((res) => {
        const name = res?.data?.currentPlan?.name;
        if (name) setCurrentPlanName(name);
      })
      .catch(() => { /* fall back to plan_tier */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    fullName: user.name         || '',
    company:  user.company_name || '',
    jobRole:  user.job_role     || '',
    bio:      user.bio          || '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState('');

  const handleChange = (e) => {
    setError('');
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await updateProfile({
        name:         form.fullName,
        company_name: form.company,
        job_role:     form.jobRole,
        bio:          form.bio,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const initials = form.fullName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-5">
      {/* Profile card */}
      <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
        {/* Banner */}
        <div className="h-24 bg-gradient-to-r from-primary via-violet-500 to-indigo-400 relative" />

        {/* Avatar + info */}
        <div className="px-6 pb-5">
          {/* Avatar — overlaps banner */}
          <div className="relative -mt-10 mb-3 w-fit">
            <div className="w-20 h-20 rounded-full bg-primary ring-4 ring-white flex items-center justify-center text-white text-2xl font-bold select-none shadow-md">
              {initials}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-brand-text leading-tight">{form.fullName || "—"}</h2>
                {user.is_email_verified && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-success font-semibold bg-success/10 px-2 py-0.5 rounded-full">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <p className="text-sm text-brand-muted mt-0.5">{user.email}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {form.jobRole && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <Briefcase className="w-3.5 h-3.5" /> {form.jobRole}
                  </span>
                )}
                {form.company && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <Building2 className="w-3.5 h-3.5" /> {form.company}
                  </span>
                )}
                {/* joinedDate && (
                  <span className="flex items-center gap-1 text-xs text-brand-muted">
                    <CalendarDays className="w-3.5 h-3.5" /> Joined {joinedDate}
                  </span>
                ) */}
              </div>
            </div>
            {currentPlanName && (
              <span className="self-start inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full capitalize whitespace-nowrap">
                <ShieldCheck className="w-3.5 h-3.5" /> {currentPlanName} Plan
              </span>
            )}
          </div>

          {/* Stats row - commented out
          <div className="mt-4 grid grid-cols-3 divide-x divide-surface-border border border-surface-border rounded-xl overflow-hidden">
            <div className="flex flex-col items-center py-3 gap-0.5 bg-gray-50/60">
              <p className="text-lg font-bold text-brand-text">{user.total_analyses ?? 0}</p>
              <p className="text-[11px] text-brand-muted">Total Analysis</p>
            </div>
            <div className="flex flex-col items-center py-3 gap-0.5 bg-gray-50/60">
              <p className="text-lg font-bold text-brand-text">{user.this_month_analyses ?? 0}</p>
              <p className="text-[11px] text-brand-muted">This Month</p>
            </div>
            <div className="flex flex-col items-center py-3 gap-0.5 bg-gray-50/60">
              <p className="text-lg font-bold text-brand-text">{user.analyses_remaining ?? "∞"}</p>
              <p className="text-[11px] text-brand-muted">Remaining</p>
            </div>
          </div>
          */}
        </div>
      </div>

      {/* Personal information form */}
      <div className="bg-white border border-surface-border rounded-2xl p-6">
        <h3 className="text-base font-semibold text-brand-text mb-5">Personal Information</h3>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <FieldLabel>Full Name</FieldLabel>
              <div className="relative">
                <IconWrap><User className="w-4 h-4" /></IconWrap>
                <input
                  type="text" name="fullName" value={form.fullName} onChange={handleChange}
                  placeholder="John Doe" className={inputClass}
                />
              </div>
            </div>

            {/* Email — read only */}
            <div>
              <FieldLabel>Email Address</FieldLabel>
              <div className="relative">
                <IconWrap><Mail className="w-4 h-4" /></IconWrap>
                <input
                  type="email" name="email" value={user.email || ''} readOnly
                  className="w-full pl-9 pr-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-muted bg-gray-100 cursor-not-allowed select-none focus:outline-none transition"
                />
              </div>
              <p className="mt-1 text-xs text-brand-light">Email cannot be changed.</p>
            </div>

            {/* Company */}
            <div>
              <FieldLabel>Company</FieldLabel>
              <div className="relative">
                <IconWrap><Building2 className="w-4 h-4" /></IconWrap>
                <input
                  type="text" name="company" value={form.company} onChange={handleChange}
                  placeholder="Acme Corporation" className={inputClass}
                />
              </div>
            </div>

            {/* Job Role */}
            <div>
              <FieldLabel>Job Role</FieldLabel>
              <div className="relative">
                <IconWrap><Briefcase className="w-4 h-4" /></IconWrap>
                <input
                  type="text" name="jobRole" value={form.jobRole} onChange={handleChange}
                  placeholder="e.g. Privacy Officer" className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <FieldLabel>Bio</FieldLabel>
            <textarea
              name="bio" value={form.bio} onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-y"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-2.5">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-success/10 border border-success/30 text-success text-sm rounded-lg px-4 py-2.5">
              Profile updated successfully.
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving…' : success ? 'Saved ✓' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Security tab ──────────────────────────────────────────────────────────────
const SecurityTab = () => {
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState({ currentPassword: false, newPassword: false, confirmPassword: false });

  const togglePw = (name) => setShowPw((s) => ({ ...s, [name]: !s[name] }));

  const handleChange = (e) => {
    setError("");
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Front-end validation ──────────────────────────────────────────────
    if (form.newPassword !== form.confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (!isPasswordStrong(form.newPassword)) {
      setError("Password must be at least 8 characters, include an uppercase letter, a number, and a special character.");
      return;
    }

    // ── Call API ──────────────────────────────────────────────────────────
    setLoading(true);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);
      setSuccess(true);
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const pwInput = (name, label, value, showStrength = false) => (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <IconWrap><Lock className="w-4 h-4" /></IconWrap>
        <input
          type={showPw[name] ? "text" : "password"} name={name} value={value} onChange={handleChange}
          placeholder="••••••••"
          className={inputClass + " pr-10"}
        />
        <button
          type="button"
          onClick={() => togglePw(name)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-light hover:text-brand-muted transition"
        >
          {showPw[name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {showStrength && <PasswordStrengthIndicator password={value} />}
    </div>
  );

  return (
    <div className="bg-white border border-surface-border rounded-2xl p-6">
      <h3 className="text-base font-semibold text-brand-text mb-5">Change Password</h3>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-2.5">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-success/10 border border-success/30 text-success text-sm rounded-lg px-4 py-2.5">
            Password updated successfully.
          </div>
        )}
        {pwInput("currentPassword", "Current Password", form.currentPassword)}
        {pwInput("newPassword",     "New Password",     form.newPassword,     true)}
        {pwInput("confirmPassword", "Confirm New Password", form.confirmPassword)}
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const TABS = ["Profile", "Security"];

const ProfilePage = () => {
  const [tab, setTab] = useState("Profile");

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-brand-text">Profile Settings</h1>
        <p className="text-sm text-brand-muted mt-0.5">
          Manage your account, preferences, and security.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-surface-border mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t
                ? "border-primary text-primary"
                : "border-transparent text-brand-muted hover:text-brand-text"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Profile"  && <ProfileTab />}
      {tab === "Security" && <SecurityTab />}
    </div>
  );
};

export default ProfilePage;
